import { useEffect } from 'react';

/**
 * Tag the current route `noindex,nofollow` for as long as the calling page is
 * mounted.
 *
 * For pages that can never render correctly from a cold search landing —
 * post-conversion screens, and email deep links whose URL carries a token.
 * `robots.txt` has matching Disallow rules (defense in depth); this covers the
 * crawlers that ignore it.
 *
 * Dedupe: only create and remove a tag if WE inserted it. Protects against
 * (a) StrictMode dev double-mount briefly stacking two tags and (b) an HMR
 * cycle that throws between createElement and append, leaving a stale tag in
 * the head. The `ownedMeta` flag scopes cleanup to our own insertion, so a
 * meta tag we didn't create is never removed.
 */
export function useNoIndexMeta(): void {
  useEffect(() => {
    const existing = document.head.querySelector('meta[name="robots"]');
    let ownedMeta: HTMLMetaElement | null = null;
    if (!existing) {
      ownedMeta = document.createElement('meta');
      ownedMeta.name = 'robots';
      ownedMeta.content = 'noindex,nofollow';
      document.head.appendChild(ownedMeta);
    }
    return () => {
      if (ownedMeta && ownedMeta.parentNode === document.head) {
        document.head.removeChild(ownedMeta);
      }
    };
  }, []);
}
